import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { Product } from '../../../analytics-core/interfaces/configuration.interface';
import { Widget } from '../../../analytics-core/interfaces/widget.interface';
import {
	EVENT_EMITTERS,
	PRINTABLE_ACCESS_KEYS,
	PRISTINE_PERMISSIONS,
	CSV_TOOLTIP
} from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { AnalyticsMixpanelService } from '../../../analytics/services/analytics.mixpanel.service';
import { PERMISSION } from '../../../analytics-core/interfaces/permission.interface';
import * as _ from 'underscore';

@Component({
	selector: 'app-product-csv-download',
	styleUrls: ['./product-csv-download.component.css'],
	templateUrl: './product-csv-download.component.html'
})
export class ProductCsvDownloadComponent implements OnInit, OnDestroy {
	@Input() overviewCsvKey: boolean = false;
	@Input() hasTooltip: boolean = false;
	public key: string;
	public filename: string;
	public productname: string;
	public reloadSubscription;
	public showDownloadIcon = true;
	public products: Product[];
	public showProductDownloadIcon: any = false;
	public productDownloadConfigData: object[] = [];
	public currentPath: string;
	public disabledDownloadButton: boolean = true;
	public overviewDisabledDownloadButton: boolean = true;
	public hasOverviewCSVPermission: boolean = false;
	public newTabDetails = [];
	public requestSubscription: Subject<any>;
	public productCampaignsActiveCount: any = 0;
	public isR6: boolean = false;
	public isR1301: boolean = false;
	public isR102: boolean = false;
	public isR103: boolean = false;
	public isR2: boolean = false;
	public hasYoutubeViewPermission: boolean = false;
	public overviewReportSubscription;
	public overviewToolTipMsg: string = '';
	constructor(
		protected analyticsService: AnalyticsService,
		protected analyticsDataModel: AnalyticsDataModel,
		protected analyticsMixpanelService: AnalyticsMixpanelService
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe(() => {
			const advertisers = this.analyticsDataModel.selectedAdvertisers;
			const campaigns = this.analyticsDataModel.selectedCampaigns;
			this.showDownloadIcon = !(advertisers.length === 0 || campaigns.length === 0);
			this.overviewDisabledDownloadButton = this.analyticsDataModel.overViewReportDetails?.length > 0 ? false : true;
		});
		this.products = this.analyticsDataModel.config.products;
		this.getProductDownloadConfig();
		this.overviewReportSubscription = EmitterService.get(EVENT_EMITTERS.UPDATE_OVERVIEW_REPORT_DETAILS).subscribe(
			(response) => {
				this.overviewDisabledDownloadButton = this.analyticsDataModel.overViewReportDetails?.length > 0 ? false : true;
			}
		);
		EmitterService.get(EVENT_EMITTERS.PRODUCT_DOWNLOAD_STATUS_CHECK).subscribe(() => {
			this.disabledDownloadButton = true;
			const STATUS_QKEY = this.getStatusQKey();
			if (STATUS_QKEY) {
				this.requestData(STATUS_QKEY);
			}
		});
	}

	ngOnInit(): void {
		this.disabledDownloadButton = true;
		this.isR103 =
			this.analyticsDataModel.config.directoryName == 'default-r103' ||
			this.analyticsDataModel.config.directoryName == 'entravision-r103'
				? true
				: false;
		this.key = this.overviewCsvKey
			? 'overview'
			: this.isR103 && this.key.toLowerCase() == 'tv'
			? 'Television'
			: this.key;
		this.overviewToolTipMsg = this.hasTooltip ? CSV_TOOLTIP.TOOLTIP : '';
		this.isR6 = this.analyticsDataModel.config.version.toLowerCase() == 'r6' ? true : false;
		this.isR1301 = this.analyticsDataModel.config.directoryName == 'default-r1301' ? true : false;
		this.isR102 =
			this.analyticsDataModel.config.directoryName == 'default-r102' ||
			this.analyticsDataModel.config.directoryName == 'default-r1' ||
			this.analyticsDataModel.config.directoryName == 'default-r101' ||
			this.analyticsDataModel.config.directoryName == 'midco' ||
			this.analyticsDataModel.config.directoryName == 'hearst-r1' ||
			this.analyticsDataModel.config.directoryName == 'default-r2'
				? true
				: false;
		this.isR2 = this.analyticsDataModel.config.version.toLowerCase() == 'r2';
		if (this.key.toLocaleLowerCase() == 'overview') {
			setTimeout(() => {
				this.overviewDisabledDownloadButton = this.analyticsDataModel.overViewReportDetails?.length > 0 ? false : true;
			}, 5000);

			const userPermissions: any[] = this.analyticsDataModel.setupData.permissions;
			this.hasOverviewCSVPermission =
				_.indexOf(userPermissions, PERMISSION[PERMISSION.hasOverviewCSVAccess]) == -1 ? false : true;
		}
		const STATUS_QKEY = this.getStatusQKey();
		if (STATUS_QKEY) {
			this.requestData(STATUS_QKEY);
		}
		if (this.productDownloadConfigData.length && this.key) {
			this.showProductDownloadIcon = false;
			this.showProductDownloadIcon = this.productDownloadConfigData.filter((obj) => {
				return obj['productName'] === this.key;
			});
			this.showDownloadIcon = !this.showProductDownloadIcon.length;
		}
		this.currentPath = this.analyticsDataModel.getCurrentPath();
	}

	requestData(statusKey: string): void {
		if (this.analyticsDataModel.selectedCampaigns.length <= 0) {
			this.disabledDownloadButton = true;
		}
		if (statusKey !== '') {
			if (this.requestSubscription) {
				this.requestSubscription.unsubscribe();
			}
			this.requestSubscription = this.analyticsService.processWidgetRequest(statusKey).subscribe((response: any) => {
				if (response.success) {
					if (response.data[0].status) {
						this.disabledDownloadButton = false;
					}
				}
			});
		}
	}

	getStatusQKey(): string {
		let qkey = '';
		this.analyticsDataModel.config.navWidgets.forEach((navWidget) => {
			if (navWidget.widgetPath.toLowerCase() === this.analyticsDataModel.getCurrentPath().toLowerCase()) {
				qkey = navWidget.query.key;
			}
		});
		return qkey;
	}

	getProductDownloadConfig(): void {
		this.products.forEach((product: Product) => {
			if (product.enableProductDownloadConfig) {
				product.widgets
					.sort((widget1, widget2) => widget1.displayOrder - widget2.displayOrder)
					.forEach((widget: Widget) => {
						const widgetInfo = {};
						const downloadConfig = JSON.parse(decodeURI(widget.downloadConfig));
						if (!UtilityService.isUndefinedOrNull(downloadConfig) && downloadConfig.enableDownload) {
							widgetInfo['productName'] = product.productName;
							widgetInfo['widgetName'] = widget.widget;
							widgetInfo['tabName'] = downloadConfig.fileName
								? downloadConfig.fileName
								: `${product.productName}_${widget.widget}`;
							widgetInfo['qkey'] = downloadConfig.csvQueryKey ? downloadConfig.csvQueryKey : widget.query.key;
							widgetInfo['columns'] = widget.downloadCSVColumns !== '{}' ? widget.downloadCSVColumns : null;
							if (downloadConfig.permission && downloadConfig.permission['flags']?.length > 0) {
								let isPermissionEnabled = !downloadConfig.permission['flags']
									.map((elem) => {
										return this.analyticsDataModel
											.checkComponentForPermission({ permission: { flags: [elem] } })
											.toString();
									})
									.includes('false');
								if (isPermissionEnabled) {
									widgetInfo['newtab_key'] = downloadConfig.newTabCsvQueryKey;
									widgetInfo['newtab_name'] = downloadConfig.newTabCsvFileName;
								}
							} else {
								widgetInfo['newtab_key'] = downloadConfig.newTabCsvQueryKey;
								widgetInfo['newtab_name'] = downloadConfig.newTabCsvFileName;
							}
							this.productDownloadConfigData.push(widgetInfo);
						}
					});
			}
		});
	}

	downloadDataInCSV(event: any): void {
		event.preventDefault();
		this.analyticsService.downloadCSV(this.key, this.filename);
	}

	downloadProductDataXLSX(event: any): void {
		const permission: any[] = this.analyticsDataModel.setupData.permissions;
		this.hasYoutubeViewPermission = _.indexOf(permission, PERMISSION[PERMISSION.hasYoutubeViews]) == -1 ? false : true;
		if (this.key.toLocaleLowerCase() == 'overview' && this.hasOverviewCSVPermission) {
			this.overviewDisabledDownloadButton = this.analyticsDataModel.overViewReportDetails?.length > 0 ? false : true;
			this.analyticsDataModel.overViewReportDetails.forEach((key: any) => {
				if (this.hasYoutubeViewPermission && this.isR6) {
					key = key == 'Pre roll/ Youtube' ? 'Pre roll' : key;
				} else if (!this.hasYoutubeViewPermission && this.isR6) {
					key = key == 'Pre Roll' ? 'Pre roll' : key;
				}
				key = key.toLocaleLowerCase();
				key = this.isR103 && key.toLowerCase() == 'tv' ? 'television' : key.toLocaleLowerCase();
				if (this.disabledDownloadButton || ((this.isR6 || this.isR2) && !this.disabledDownloadButton)) {
					event.preventDefault();
					const productdownloadconfig = this.productDownloadConfigData.filter((obj) => {
						return obj['productName'].toLocaleLowerCase() === key;
					});
					this.newTabDetails = productdownloadconfig
						.filter((obj) => obj['newtab_key'] && obj['newtab_name'])
						.map((obj) => {
							return {
								newtab_key: obj['newtab_key'],
								newtab_name: obj['newtab_name']
							};
						});

					let productFileName = this.products.filter((product) => product.productName.toLocaleLowerCase() === key)[0]
						?.productCsvFilename;
					productFileName = productFileName ? productFileName : key ? key + ' Data Download ' : undefined;
					if (productFileName) {
						let newFiles = this.newTabDetails.length > 0 ? this.newTabDetails : null;
						this.analyticsService.downloadProductXLSX(
							key,
							productFileName,
							JSON.stringify(productdownloadconfig),
							newFiles
						);
					} else {
						console.log('Missing the File name of the Product Data Download!!');
					}
				}
			});
		} else if (this.key.toLocaleLowerCase() != 'overview') {
			if (!this.disabledDownloadButton) {
				if (this.hasYoutubeViewPermission && this.isR6) {
					this.key = this.key == 'Pre roll/ Youtube' ? 'Pre roll' : this.key;
				} else if (!this.hasYoutubeViewPermission && this.isR6) {
					this.key = this.key == 'Pre Roll' ? 'Pre roll' : this.key;
				}
				event.preventDefault();
				const productdownloadconfig = this.productDownloadConfigData.filter((obj) => {
					return obj['productName'] === this.key;
				});
				this.newTabDetails = productdownloadconfig
					.filter((obj) => obj['newtab_key'] && obj['newtab_name'])
					.map((obj) => {
						return {
							newtab_key: obj['newtab_key'],
							newtab_name: obj['newtab_name']
						};
					});
				let productFileName = this.products.filter((product) => product.productName === this.key)[0].productCsvFilename;
				productFileName = productFileName ? productFileName : this.key ? this.key + ' Data Download ' : undefined;
				if (productFileName) {
					let newFiles = this.newTabDetails.length > 0 ? this.newTabDetails : null;
					this.analyticsService.downloadProductXLSX(
						this.key,
						productFileName,
						JSON.stringify(productdownloadconfig),
						newFiles
					);
				} else {
					console.log('Missing the File name of the Product Data Download!!');
				}
			}
		}

		if (!this.overviewDisabledDownloadButton || !this.disabledDownloadButton) {
			let product;
			this.analyticsDataModel.config.products.forEach((item) => {
				if (item.productURL === this.analyticsDataModel.getCurrentPath()) {
					product = item;
				}
			});

			let event_name =
				this.key.toLocaleLowerCase() == 'overview'
					? 'Analytics / One Click Download / Download Bulk Excel'
					: product.productName + ' Bulk Download';
			this.productCampaignsActiveCount = this.analyticsMixpanelService.productSelectedActiveCampaign(
				this.key.toLocaleLowerCase()
			);
			this.analyticsMixpanelService.mixpanelBulkDownloadData(event_name, this.productCampaignsActiveCount);
		}
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.overviewReportSubscription) {
			this.overviewReportSubscription.unsubscribe();
		}
	}
}
